import { CommonModule } from '@angular/common';
import {
  Component,
  ElementRef,
  OnDestroy,
  ViewChild,
  inject,
  OnInit,
} from '@angular/core';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import {
  AiActionItemsDataOutput,
  AiAgentStepStreamEvent,
  AiAssistantBlock,
  AiChatMessage,
  AiChatStreamEvent,
  AiPromptAudit,
  AiSuggestion,
  AiTaskContextDataOutput,
  AiTaskLineageDataOutput,
} from '../../models/ai-assistant.models';
import { AiAssistantApiService } from '../../services/ai-assistant-api.service';
import { AiAssistantMockService } from '../../services/ai-assistant-mock.service';
import { AiAssistantResponseMapperService } from '../../services/ai-assistant-response-mapper.service';
import { AiChatHeaderComponent } from '../ai-chat-header/ai-chat-header.component';
import { AiMessageInputComponent } from '../ai-message-input/ai-message-input.component';
import { AiMessageListComponent } from '../ai-message-list/ai-message-list.component';
import {
  AiAgent,
  AiWelcomeStateComponent,
  AGENTS,
} from '../ai-welcome-state/ai-welcome-state.component';
import { AuditPromptDialogComponent } from '../audit-prompt-dialog/audit-prompt-dialog.component';

type AiSidebarMode = 'collapsed' | 'panel' | 'expanded';

const SIDEBAR_RECENT_CHATS = [
  { id: '2', title: 'What are my critical risks?', time: '15m ago' },
  { id: '1', title: 'Show top 5 cloud assets with exposure', time: '9:00 AM' },
  { id: '3', title: 'Tasks overdue this month', time: '1h ago' },
  { id: '4', title: 'Policy coverage for NIST AI...', time: '3h ago' },
  { id: '5', title: 'Show open action items', time: 'Yesterday' },
];

const STATIC_AI_PROGRESS_STEPS = [
  'Understanding your request...',
  'Checking relevant controls and tasks...',
  'Running investigation tools...',
  'Preparing visual results...',
  'Composing response...',
];

const EVA_NUDGE_DELAY_MS = 300000;
const EVA_NUDGE_VISIBLE_MS = 3600;
const EVA_NUDGE_BUBBLE_DELAY_MS = 260;
const EVA_NUDGE_MESSAGES = [
  'I can summarize overdue tasks and action items.',
  'Ask me to map risks to controls, assets, and owners.',
  'I can pull out the highest priority gaps on this platform.',
  'Want a quick compliance or framework summary?',
  'I can explain task status, evidence needs, and next steps.',
  'Ask me for asset-linked risks across the dashboard.',
  'I can help you trace controls, tasks, and remediation work.',
];

@Component({
  selector: 'app-ai-sidebar',
  standalone: true,
  imports: [
    CommonModule,
    AiChatHeaderComponent,
    AiMessageInputComponent,
    AiMessageListComponent,
    AiWelcomeStateComponent,
    AuditPromptDialogComponent,
  ],
  templateUrl: './ai-sidebar.component.html',
  styleUrl: './ai-sidebar.component.css',
})
export class AiSidebarComponent implements OnDestroy, OnInit {
  @ViewChild('sidebarBody') private sidebarBody?: ElementRef<HTMLElement>;

  private readonly assistantService = inject(AiAssistantMockService);
  private readonly assistantApiService = inject(AiAssistantApiService);
  private readonly responseMapper = inject(AiAssistantResponseMapperService);
  private readonly router = inject(Router);
  private readonly evaLogoSize = { width: 108, height: 90 };
  private readonly evaLogoMargin = 12;
  private evaLogoDragOffset = { x: 0, y: 0 };
  private isEvaLogoDragging = false;
  private readonly handleEvaLogoPointerMove = (event: PointerEvent): void => {
    this.moveEvaLogo(event);
  };
  private readonly handleEvaLogoPointerUp = (): void => {
    this.stopEvaLogoDrag();
  };
  private chatSubscription?: Subscription;
  private progressTimer?: ReturnType<typeof setInterval>;
  private scrollRafId?: number;
  private pendingTaskContextData: AiTaskContextDataOutput | null = null;
  private pendingActionItemsData: AiActionItemsDataOutput | null = null;
  private pendingTaskLineageData: AiTaskLineageDataOutput | null = null;
  private nudgeStartTimer?: ReturnType<typeof setTimeout>;
  private nudgeHideTimer?: ReturnType<typeof setTimeout>;
  private nudgeBubbleTimer?: ReturnType<typeof setTimeout>;
  private progressIndex = 0;
  private nudgeMessageIndex = 0;
  private activeAssistantMessageId: string | null = null;
  private activeUserQuery = '';

  mode: AiSidebarMode = 'collapsed';
  messages: AiChatMessage[] = [];
  activeAgent: AiAgent = AGENTS[0];

  onAgentChanged(agent: AiAgent): void {
    this.activeAgent = agent;
  }
  readonly recentChats = SIDEBAR_RECENT_CHATS;
  suggestions: AiSuggestion[] = this.assistantService.getWelcomeSuggestions();
  selectedAudit: AiPromptAudit | null = null;
  evaLogoPosition: { left: number; top: number } | null = null;
  showEvaNudge = false;
  showEvaNudgeBubble = false;
  evaNudgeMessage = EVA_NUDGE_MESSAGES[0];
  fabPosition: { x: number; y: number } | null = null;
  isFabDragging = false;
  private fabDragStart = { x: 0, y: 0 };
  private hasMoved = false;

  get isOpen(): boolean {
    return this.mode !== 'collapsed';
  }

  get isExpanded(): boolean {
    return this.mode === 'expanded';
  }

  ngOnInit(): void {
    this.scheduleEvaNudge();
    const saved = localStorage.getItem('octy-fab-position');
    if (saved) {
      try {
        const pos = JSON.parse(saved);
        setTimeout(() => {
          const contentElement = document.querySelector('.shell__content');
          const contentRect = contentElement
            ? contentElement.getBoundingClientRect()
            : null;

          let minX = 10;
          let minY = 10;
          let maxX = window.innerWidth - 64 - 10;
          let maxY = window.innerHeight - 64 - 10;

          if (contentRect) {
            minX = contentRect.left + 10;
            maxX = contentRect.right - 64 - 10;
            minY = contentRect.top + 10;
            maxY = contentRect.bottom - 64 - 10;
          }

          this.fabPosition = {
            x: Math.min(Math.max(pos.x, minX), maxX),
            y: Math.min(Math.max(pos.y, minY), maxY),
          };
        }, 100);
      } catch (e) {
        // ignore
      }
    }
  }

  toggle(): void {
    this.mode = this.isOpen ? 'collapsed' : 'panel';
    this.resetEvaNudgeCycle();
  }

  openPanel(): void {
    this.mode = 'panel';
    this.resetEvaNudgeCycle();
  }

  openHistory(): void {
    this.mode = 'panel';
    this.resetEvaNudgeCycle();
  }

  startNewPrompt(): void {
    this.cancelActiveChat();
    this.messages = [];
    this.activeAssistantMessageId = null;
    this.activeUserQuery = '';
    this.mode = 'panel';
    this.resetEvaNudgeCycle();
  }

  collapse(): void {
    this.mode = 'collapsed';
    this.resetEvaNudgeCycle();
  }

  expand(): void {
    this.mode = this.isExpanded ? 'panel' : 'expanded';
    this.resetEvaNudgeCycle();
  }

  submitQuery(
    payload:
      | string
      | {
          query: string;
          agentId: string;
          agentName: string;
          agentColor: string;
          agentIcon: string;
        },
  ): void {
    let query: string;
    let agentId: string | undefined;
    let agentName: string | undefined;
    let agentColor: string | undefined;
    let agentIcon: string | undefined;

    if (typeof payload === 'string') {
      query = payload;
    } else {
      query = payload.query;
      agentId = payload.agentId;
      agentName = payload.agentName;
      agentColor = payload.agentColor;
      agentIcon = payload.agentIcon;
      const matched = AGENTS.find((a) => a.id === agentId);
      if (matched) this.activeAgent = matched;
    }

    if (!this.isOpen) {
      this.mode = 'panel';
    }

    this.resetEvaNudgeCycle();
    this.cancelActiveChat();
    this.activeUserQuery = query;

    const userMessage: AiChatMessage = {
      id: this.nextId('user'),
      role: 'user',
      text: query,
      createdAt: new Date().toISOString(),
      agentId,
      agentName,
      agentColor,
      agentIcon,
    };
    const assistantMessage: AiChatMessage = {
      id: this.nextId('assistant'),
      role: 'assistant',
      text: '',
      blocks: [],
      streaming: true,
      loadingLabel: STATIC_AI_PROGRESS_STEPS[0],
      createdAt: new Date().toISOString(),
    };

    this.activeAssistantMessageId = assistantMessage.id;
    this.messages = [...this.messages, userMessage, assistantMessage];
    this.startStaticProgress();
    this.scheduleScrollToActiveResponse();

    this.chatSubscription = this.assistantApiService
      .sendMessage(query)
      .subscribe({
        next: (event) => this.handleStreamEvent(event),
        error: (error: unknown) => this.handleStreamError(error),
        complete: () => this.stopStaticProgress(),
      });
  }

  openAudit(audit: AiPromptAudit): void {
    this.selectedAudit = audit;
  }

  closeAudit(): void {
    this.selectedAudit = null;
  }

  openSettings(): void {
    this.router.navigateByUrl('/organization-settings');
    this.mode = 'collapsed';
    this.resetEvaNudgeCycle();
  }

  openPanelFromNudge(): void {
    this.openPanel();
  }

  startFabDrag(event: PointerEvent): void {
    if (event.button !== 0) return; // Only drag with left click

    const fabElement = event.currentTarget as HTMLElement;
    const rect = fabElement.getBoundingClientRect();

    this.isFabDragging = true;
    this.hasMoved = false;
    this.fabDragStart = {
      x: event.clientX - rect.left,
      y: event.clientY - rect.top,
    };

    fabElement.setPointerCapture?.(event.pointerId);

    const contentElement = document.querySelector('.shell__content');
    const contentRect = contentElement
      ? contentElement.getBoundingClientRect()
      : null;

    const onPointerMove = (e: PointerEvent): void => {
      if (!this.isFabDragging) return;

      const dx = e.clientX - event.clientX;
      const dy = e.clientY - event.clientY;
      if (Math.abs(dx) > 3 || Math.abs(dy) > 3) {
        this.hasMoved = true;
      }

      const newX = e.clientX - this.fabDragStart.x;
      const newY = e.clientY - this.fabDragStart.y;

      let minX = 10;
      let minY = 10;
      let maxX = window.innerWidth - rect.width - 10;
      let maxY = window.innerHeight - rect.height - 10;

      if (contentRect) {
        minX = contentRect.left + 10;
        maxX = contentRect.right - rect.width - 10;
        minY = contentRect.top + 10;
        maxY = contentRect.bottom - rect.height - 10;
      }

      this.fabPosition = {
        x: Math.min(Math.max(newX, minX), maxX),
        y: Math.min(Math.max(newY, minY), maxY),
      };
    };

    const onPointerUp = (e: PointerEvent): void => {
      this.isFabDragging = false;
      fabElement.releasePointerCapture?.(e.pointerId);
      document.removeEventListener('pointermove', onPointerMove);
      document.removeEventListener('pointerup', onPointerUp);
      document.removeEventListener('pointercancel', onPointerCancel);
      if (this.fabPosition) {
        localStorage.setItem(
          'octy-fab-position',
          JSON.stringify(this.fabPosition),
        );
      }
    };

    const onPointerCancel = (e: PointerEvent): void => {
      this.isFabDragging = false;
      fabElement.releasePointerCapture?.(e.pointerId);
      document.removeEventListener('pointermove', onPointerMove);
      document.removeEventListener('pointerup', onPointerUp);
      document.removeEventListener('pointercancel', onPointerCancel);
    };

    document.addEventListener('pointermove', onPointerMove);
    document.addEventListener('pointerup', onPointerUp);
    document.addEventListener('pointercancel', onPointerCancel);
  }

  onFabClick(): void {
    if (!this.hasMoved) {
      this.openPanel();
    }
  }

  startEvaLogoDrag(event: PointerEvent): void {
    if (!this.isExpanded) {
      return;
    }

    const logoElement = event.currentTarget as HTMLElement;
    const logoRect = logoElement.getBoundingClientRect();

    this.isEvaLogoDragging = true;
    this.evaLogoDragOffset = {
      x: event.clientX - logoRect.left,
      y: event.clientY - logoRect.top,
    };
    this.evaLogoPosition = {
      left: logoRect.left,
      top: logoRect.top,
    };

    logoElement.setPointerCapture?.(event.pointerId);
    document.addEventListener('pointermove', this.handleEvaLogoPointerMove);
    document.addEventListener('pointerup', this.handleEvaLogoPointerUp, {
      once: true,
    });
  }

  ngOnDestroy(): void {
    this.cancelActiveChat();
    this.clearEvaNudgeTimers();
    this.stopEvaLogoDrag();
    if (this.scrollRafId !== undefined) clearTimeout(this.scrollRafId);
  }

  private handleStreamEvent(event: AiChatStreamEvent): void {
    switch (event.type) {
      case 'token':
        this.appendAssistantToken(event.token);
        break;
      case 'status':
        this.updateActiveAssistantMessage({ loadingLabel: event.message });
        this.scheduleScrollToActiveResponse();
        break;
      case 'tool_result':
        this.appendAssistantBlocks(
          this.responseMapper.mapToolResultToBlocks(event),
        );
        this.updateActiveAssistantMessage({
          loadingLabel: 'Visual result generated.',
        });
        this.scheduleScrollToActiveResponse();
        break;
      case 'task_context_data':
        this.pendingTaskContextData = event.output;
        break;
      case 'action_items_data':
        this.pendingActionItemsData = event.output;
        break;
      case 'task_lineage_data':
        this.pendingTaskLineageData = event.output;
        break;
      case 'agent_step':
        this.handleAgentStep(event);
        break;
      case 'done': {
        this.stopStaticProgress();
        const taskContextBlock = this.responseMapper.mapDoneToTaskContextBlock(
          event,
          this.pendingTaskContextData,
          !!this.pendingTaskLineageData,
        );
        const taskLineageBlock = this.responseMapper.mapDoneToTaskLineageBlock(
          event,
          this.pendingTaskLineageData,
        );
        const actionItemsBlock = this.pendingActionItemsData
          ? this.responseMapper.mapDoneToActionItemsBlock(
              this.pendingActionItemsData,
            )
          : null;
        const richBlocks = [taskContextBlock, taskLineageBlock].filter(
          (b): b is NonNullable<typeof b> => b !== null,
        );
        const rawAnswer =
          event.answer || this.getActiveAssistantMessage()?.text || '';
        const answerText = actionItemsBlock
          ? this.cleanActionItemsAnswer(rawAnswer)
          : rawAnswer;
        // Multiple branches each narrate independently (deterministic or LLM,
        // per their own reasoning profile) — render each as its own section
        // instead of one blended paragraph. Single-section responses keep the
        // existing flat-text rendering unchanged.
        const branchNarrationBlocks =
          this.responseMapper.mapDoneToBranchNarrationBlocks(event);
        const usingSections = branchNarrationBlocks.length > 1;
        this.updateActiveAssistantMessage({
          text: richBlocks.length > 0 || usingSections ? '' : answerText,
          streaming: false,
          loadingLabel: undefined,
          audit: this.responseMapper.createAudit(this.activeUserQuery, {
            done: event,
          }),
        });
        if (richBlocks.length > 0) this.appendAssistantBlocks(richBlocks);
        if (actionItemsBlock) this.appendAssistantBlocks([actionItemsBlock]);
        if (usingSections) this.appendAssistantBlocks(branchNarrationBlocks);
        this.pendingTaskContextData = null;
        this.pendingActionItemsData = null;
        this.pendingTaskLineageData = null;
        this.activeAssistantMessageId = null;
        this.scheduleScrollToActiveResponse();
        this.resetEvaNudgeCycle();
        break;
      }
      case 'error':
        this.handleStreamError(event.message);
        break;
    }
  }

  private appendAssistantToken(token: string): void {
    const currentText = this.getActiveAssistantMessage()?.text || '';
    this.updateActiveAssistantMessage({
      text: `${currentText}${token}`,
      loadingLabel: undefined,
    });
    this.scrollToBottomInstant();
  }

  private appendAssistantBlocks(blocks: AiAssistantBlock[]): void {
    const currentBlocks = this.getActiveAssistantMessage()?.blocks || [];
    const currentBlockIds = new Set(currentBlocks.map((block) => block.id));
    const nextBlocks = blocks.filter((block) => !currentBlockIds.has(block.id));

    if (!nextBlocks.length) {
      return;
    }

    this.updateActiveAssistantMessage({
      blocks: [...currentBlocks, ...nextBlocks],
    });
  }

  private handleAgentStep(event: AiAgentStepStreamEvent): void {
    // Real backend turn data has arrived — stop the fake fixed-interval timer
    // for good so it can't overwrite this with a canned string.
    this.stopStaticProgress();

    const activeMessage = this.getActiveAssistantMessage();
    const currentSteps = activeMessage?.steps || [];
    this.updateActiveAssistantMessage({
      steps: [...currentSteps, event],
      loadingLabel: this.describeAgentStep(event),
    });
    this.scheduleScrollToActiveResponse();
  }

  private describeAgentStep(event: AiAgentStepStreamEvent): string {
    switch (event.action) {
      case 'request_tool':
        return event.toolName
          ? `Looking up ${this.humanizeToolName(event.toolName)}...`
          : 'Running a lookup...';
      case 'request_handoff':
        return 'Checking if this needs a different specialist...';
      case 'request_clarification':
        return 'Working out what you need...';
      case 'complete':
        return 'Composing response...';
      case 'cannot_complete':
        return 'Wrapping up...';
      default:
        return 'Working on it...';
    }
  }

  private humanizeToolName(toolName: string): string {
    return toolName.replace(/_/g, ' ');
  }

  private handleStreamError(error: unknown): void {
    this.stopStaticProgress();
    this.updateActiveAssistantMessage({
      streaming: false,
      loadingLabel: undefined,
      error: error instanceof Error ? error.message : String(error),
    });
    this.pendingTaskContextData = null;
    this.pendingActionItemsData = null;
    this.pendingTaskLineageData = null;
    this.activeAssistantMessageId = null;
    this.scheduleScrollToActiveResponse();
    this.resetEvaNudgeCycle();
  }

  private startStaticProgress(): void {
    this.stopStaticProgress();
    this.progressIndex = 0;
    this.progressTimer = setInterval(() => {
      if (!this.activeAssistantMessageId) {
        this.stopStaticProgress();
        return;
      }

      this.progressIndex =
        (this.progressIndex + 1) % STATIC_AI_PROGRESS_STEPS.length;
      const activeMessage = this.getActiveAssistantMessage();
      if (activeMessage?.streaming) {
        this.updateActiveAssistantMessage({
          loadingLabel: STATIC_AI_PROGRESS_STEPS[this.progressIndex],
        });
      }
    }, 1600);
  }

  private stopStaticProgress(): void {
    if (this.progressTimer) {
      clearInterval(this.progressTimer);
      this.progressTimer = undefined;
    }
  }

  private cancelActiveChat(): void {
    this.chatSubscription?.unsubscribe();
    this.chatSubscription = undefined;
    this.pendingTaskContextData = null;
    this.pendingActionItemsData = null;
    this.pendingTaskLineageData = null;
    this.stopStaticProgress();
  }

  private scheduleEvaNudge(): void {
    this.clearEvaNudgeTimers();

    if (!this.shouldRunEvaNudge()) {
      this.showEvaNudge = false;
      this.showEvaNudgeBubble = false;
      return;
    }

    this.nudgeStartTimer = setTimeout(() => {
      this.showEvaNudgeSequence();
    }, EVA_NUDGE_DELAY_MS);
  }

  private resetEvaNudgeCycle(): void {
    this.hideEvaNudgeImmediate();
    this.scheduleEvaNudge();
  }

  private showEvaNudgeSequence(): void {
    if (!this.shouldRunEvaNudge()) {
      this.scheduleEvaNudge();
      return;
    }

    this.evaNudgeMessage = EVA_NUDGE_MESSAGES[this.nudgeMessageIndex];
    this.nudgeMessageIndex =
      (this.nudgeMessageIndex + 1) % EVA_NUDGE_MESSAGES.length;
    this.showEvaNudge = true;
    this.showEvaNudgeBubble = false;

    this.nudgeBubbleTimer = setTimeout(() => {
      if (this.showEvaNudge) {
        this.showEvaNudgeBubble = true;
      }
    }, EVA_NUDGE_BUBBLE_DELAY_MS);

    this.nudgeHideTimer = setTimeout(() => {
      this.hideEvaNudgeImmediate();
      this.scheduleEvaNudge();
    }, EVA_NUDGE_VISIBLE_MS);
  }

  private hideEvaNudgeImmediate(): void {
    this.clearEvaNudgeTimers();
    this.showEvaNudgeBubble = false;
    this.showEvaNudge = false;
  }

  private clearEvaNudgeTimers(): void {
    if (this.nudgeStartTimer) {
      clearTimeout(this.nudgeStartTimer);
      this.nudgeStartTimer = undefined;
    }

    if (this.nudgeHideTimer) {
      clearTimeout(this.nudgeHideTimer);
      this.nudgeHideTimer = undefined;
    }

    if (this.nudgeBubbleTimer) {
      clearTimeout(this.nudgeBubbleTimer);
      this.nudgeBubbleTimer = undefined;
    }
  }

  private shouldRunEvaNudge(): boolean {
    return this.mode === 'collapsed' && !this.activeAssistantMessageId;
  }

  private getActiveAssistantMessage(): AiChatMessage | undefined {
    return this.messages.find(
      (message) => message.id === this.activeAssistantMessageId,
    );
  }

  private updateActiveAssistantMessage(patch: Partial<AiChatMessage>): void {
    if (!this.activeAssistantMessageId) {
      return;
    }

    this.messages = this.messages.map((message) =>
      message.id === this.activeAssistantMessageId
        ? {
            ...message,
            ...patch,
          }
        : message,
    );
  }

  private cleanActionItemsAnswer(text: string): string {
    // Remove the **Summary** block (Matched/Returned counts) — redundant with table header.
    // Keep the opening sentence and any **Gaps** / other sections.
    return text
      .replace(/\*\*Summary\*\*[\s\S]*?(?=\*\*[A-Z]|\n##|\z)/i, '')
      .replace(/^\s*\n+/, '')
      .trim();
  }

  private getScrollContainer(): HTMLElement | null {
    const body = this.sidebarBody?.nativeElement;
    if (!body) return null;
    // In expanded mode sidebarBody has overflow:hidden — scroll lives in .ai-conv-messages
    if (this.isExpanded) {
      return (body.querySelector('.ai-conv-messages') as HTMLElement) ?? body;
    }
    return body;
  }

  private scrollToBottomInstant(): void {
    if (this.scrollRafId !== undefined) return;
    this.scrollRafId = window.setTimeout(() => {
      this.scrollRafId = undefined;
      const el = this.getScrollContainer();
      if (!el) return;
      el.scrollTop = el.scrollHeight;
    }, 0) as unknown as number;
  }

  private scheduleScrollToActiveResponse(): void {
    setTimeout(() => {
      const el = this.getScrollContainer();
      if (!el) return;
      el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
    });
  }

  private nextId(prefix: string): string {
    return `${prefix}-${Date.now()}-${crypto.randomUUID()}`;
  }

  private moveEvaLogo(event: PointerEvent): void {
    if (!this.isEvaLogoDragging) {
      return;
    }

    const maxLeft =
      window.innerWidth - this.evaLogoSize.width - this.evaLogoMargin;
    const maxTop =
      window.innerHeight - this.evaLogoSize.height - this.evaLogoMargin;
    const nextLeft = event.clientX - this.evaLogoDragOffset.x;
    const nextTop = event.clientY - this.evaLogoDragOffset.y;

    this.evaLogoPosition = {
      left: this.clamp(nextLeft, this.evaLogoMargin, maxLeft),
      top: this.clamp(nextTop, this.evaLogoMargin, maxTop),
    };
  }

  private stopEvaLogoDrag(): void {
    this.isEvaLogoDragging = false;
    document.removeEventListener('pointermove', this.handleEvaLogoPointerMove);
  }

  private clamp(value: number, min: number, max: number): number {
    return Math.min(Math.max(value, min), max);
  }
}
